import { getPaymentStatus } from "./api";
import { getStoragePaymentData, removeStoragePaymentData } from "./storage";

/**
 * @typedef {Object} StatusData
 * @property {string} header - String with header text (required)
 * @property {string} message - String with message text
 * @property {string} color - [status = 'info'] String with current status type: 'success', 'error', 'await', 'info' (required)
 */


let interval = null;


/**
 * @param {StatusData} statusData
 * @returns {void}
 */
const addPaymentStatus = ({ header, message, color = 'info' }) => {
    const statuses = ['success', 'error', 'awaiting', 'info'];

    if (!color || !statuses.includes(color) || !header || !message) return false;

    const statusElement = document.getElementById('payment-tax-status');
    if (!statusElement) return false;


    const containerElement = document.createElement('div');
    containerElement.classList.add('content-color', `content-color--${color}`);

    const rowElement = document.createElement('div');
    rowElement.classList.add('row');

    const colElement = document.createElement('div');
    colElement.classList.add('col');

    const headerElement = document.createElement('h4');
    headerElement.textContent = header;

    const textElement = document.createElement('p');
    textElement.textContent = message;

    colElement.appendChild(headerElement);
    colElement.appendChild(textElement);
    rowElement.appendChild(colElement);
    containerElement.appendChild(rowElement);

    statusElement.appendChild(containerElement);
    statusElement.classList.remove('d-none');

    statusElement.scrollIntoView({ behavior: "smooth", block: "center" });
};

const removePaymentStatus = async () => (
    new Promise((resolve, reject) => {
        const statusElement = document.getElementById('payment-tax-status');
        if (!statusElement) reject();

        statusElement && statusElement.querySelectorAll('*').forEach(child => child.remove());
        resolve();
    })
);

/**
 *
 * @param {StatusData} statusData
 * @returns {void}
 */
export const showPaymentStatus = async (statusData) => {
    await removePaymentStatus();
    addPaymentStatus(statusData);
}

export const checkPaymentStatusInterval = () => {
    const milliseconds = 1000;
    const seconds = 60;

    checkPaymentStatus();

    // Every 10 seconds
    interval = setInterval(() => {
        checkPaymentStatus();
    }, milliseconds * 15);

    // End interval after 5 minutes
    setTimeout(() => clearInterval(interval), milliseconds * seconds * 5);
}

export const checkPaymentStatus = async () => {
    try {
        const query = new URLSearchParams(window.location.search);
        const status = query.get('status') || '';
        if (!status || status !== 'awaiting') {
            removeStoragePaymentData();
            return false;
        }

        const { uuid, token } = getStoragePaymentData();
        if (!uuid || !token) return false;

        const { data } = await getPaymentStatus(uuid, token);
        if (!data) return false;

        if (data.status === 'SUCCESS') clearInterval(interval);

        showPaymentStatus(preparePaymentStatus(data.status));
    } catch (error) { }
}

export const preparePaymentStatus = status => {
    // Statuses form API:
    // 'PREPARED'; // Przygotowana płatność w systemie e-file
    // 'PENDING'; // Rozpoczęto płatność w systemie e-file
    // 'SENT_FAILURE'; // Błąd w trakcie wysłania płatności do systemu zewn.
    // 'SENT'; // Wysłano płatność do realizacji w systemie zewnętrznym
    // 'FAILURE'; // Niepowodzenie wykonania płatności
    // 'SUCCESS'; // Płatność opłacona
    // 'NOT_FOUND'; // Płatność opłacona

    if (status === 'SENT') {
        return {
            color: 'awaiting',
            header: 'Płatność przetwarzana',
            message: 'Twoja transakcja jest przetwarzana. O jej rejestracji oraz zakończeniu zostaniesz poinformowany mailowo.'
        }
    } else if (status === 'SUCCESS') {
        return {
            color: 'success',
            header: 'Płatność wykonana poprawnie',
            message: 'Twoja transakcja została zatwierdzona przez system płatniczy. Środki niebawem zostaną przekazane na Twój mikrorachunek. Dziękujemy za skorzystanie z naszych usług.'
        }
    } else if (status === 'FAILURE') {
        return {
            color: 'error',
            header: 'Płatność niepowiodła się',
            message: 'Transakcja nie powiodła się lub nie została zatwierdzona przez system płatniczy. Środki z Twojego kona nie powinny zostać pobrane. Jeśli jednak zaistniała taka sytuacja skontaktuj się z nami.'
        }
    } else if (status === 'FAILURE_SENT_FAILURE') {
        return {
            color: 'error',
            header: 'Błąd wysyłania formularza',
            message: 'Sprawdź poprawność wprowadzonych danych i spróbuj ponownie. W przypadku dalszych problemów skontaktuj się z nami.'
        }
    } else if (status === 'CUSTOM_FAILUR_PREPARED_FEEDBACK_DATA') {
        return {
            color: 'error',
            header: 'Błąd przygotowania do płatności',
            message: 'Sprawdź poprawność wprowadzonych danych i spróbuj ponownie. W przypadku dalszych problemów skontaktuj się z nami.'
        }
    } else if (status === 'CUSTOM_FAILUR_GET_PAYMENT_URL') {
        return {
            color: 'error',
            header: 'Błąd przetwarzania adresu płatności',
            message: 'Skontaktuj się z nami w celu uzyskania pomocy.'
        }
    }

    return {};
}